import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, CalendarIcon, Clock, CheckCircle, Share2, Copy, ExternalLink, MessageSquare } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Switch } from '@/components/ui/switch';
import { Separator } from '@/components/ui/separator';
import { cn } from '@/lib/utils';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { useFamily } from '@/hooks/useFamily';
import { useEvents } from '@/hooks/useEvents';
import { useGoogleCalendar } from '@/hooks/useGoogleCalendar';
import { usePublicEventPages } from '@/hooks/usePublicEventPages';
import { useToast } from '@/hooks/use-toast';
import { useCurrentSection } from '@/hooks/useCurrentSection';
import { PageHeader } from '@/components/PageHeader';
import { ImageUploader } from '@/components/ImageUploader';
import { NoFamilyMessage } from '@/components/NoFamilyMessage';
import { generateRecurringEvents, createAllDayEvent } from '@/lib/recurrenceUtils';
import { RECURRENCE_OPTIONS_ACTIVE, type RecurrencePattern } from '@/lib/recurrenceTypes';
import { generatePublicEventUrl } from '@/lib/urlUtils';
import { useUniversalContacts } from '@/hooks/useUniversalContacts';
import { EventAttendeesSelector } from '@/components/events/EventAttendeesSelector';
import { TimeSelector } from '@/components/ui/time-selector';
import { LocationInput } from '@/components/ui/location-input';

export default function CreateEvent() {
  const navigate = useNavigate();
  const { family, familyMembers } = useFamily();
  const { groupedContacts } = useUniversalContacts(family?.id);
  const { createEvent } = useEvents(family?.id);
  const { toast } = useToast();
  const { syncEventToGoogleCalendar, isConnected, connectToGoogleCalendar } = useGoogleCalendar();
  const { createOrUpdatePublicPage } = usePublicEventPages();
  const { primaryVariant } = useCurrentSection();

  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [startDate, setStartDate] = useState<Date>();
  const [startTime, setStartTime] = useState('09:00');
  const [endTime, setEndTime] = useState('10:00');
  const [eventType, setEventType] = useState<'personal' | 'family' | 'appointment' | 'birthday' | 'holiday'>('family');
  const [location, setLocation] = useState('');
  const [latitude, setLatitude] = useState<number>();
  const [longitude, setLongitude] = useState<number>();
  const [selectedAttendees, setSelectedAttendees] = useState<string[]>([]);
  const [inviteAllFamily, setInviteAllFamily] = useState(true);
  const [isAllDay, setIsAllDay] = useState(false);
  const [isRecurring, setIsRecurring] = useState(false);
  const [recurrencePattern, setRecurrencePattern] = useState<RecurrencePattern>('none');
  const [loading, setLoading] = useState(false);

  // Public sharing states
  const [isPublicSharingEnabled, setIsPublicSharingEnabled] = useState(false);
  const [publicPageUrl, setPublicPageUrl] = useState("");
  const [publicTitle, setPublicTitle] = useState("");
  const [publicDescription, setPublicDescription] = useState("");
  const [publicLocation, setPublicLocation] = useState("");
  const [rsvpEnabled, setRsvpEnabled] = useState(true);
  const [heroImageUrl, setHeroImageUrl] = useState("");

  // Update public fields when main fields change
  useEffect(() => {
    if (title && !publicTitle) setPublicTitle(title);
  }, [title, publicTitle]);

  useEffect(() => {
    if (description && !publicDescription) setPublicDescription(description);
  }, [description, publicDescription]);

  useEffect(() => {
    if (location && !publicLocation) setPublicLocation(location);
  }, [location, publicLocation]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!title || !startDate) {
      toast({
        variant: "destructive",
        title: "Erreur",
        description: "Veuillez remplir tous les champs obligatoires.",
      });
      return;
    }

    try {
      setLoading(true);

      const startDateTime = new Date(startDate);
      let endDateTime = new Date(startDate);

      if (!isAllDay) {
        const [startHours, startMinutes] = startTime.split(':').map(Number);
        const [endHours, endMinutes] = endTime.split(':').map(Number);
        
        startDateTime.setHours(startHours, startMinutes, 0, 0);
        endDateTime.setHours(endHours, endMinutes, 0, 0);
        
        if (endDateTime <= startDateTime) {
          endDateTime.setDate(endDateTime.getDate() + 1);
        }
      } else {
        // For all-day events, use createAllDayEvent helper
        const { start, end } = createAllDayEvent(startDate);
        startDateTime.setTime(start.getTime());
        endDateTime.setTime(end.getTime());
      }

      const eventData = {
        title,
        description,
        start_date: startDateTime.toISOString(),
        end_date: endDateTime.toISOString(),
        is_all_day: isAllDay,
        event_type: eventType,
        type: eventType,
        location,
        latitude,
        longitude,
        // selectedAttendees already contains profile_ids from EventAttendeesSelector
        attendees: inviteAllFamily ? [] : selectedAttendees,
        invite_all_family: inviteAllFamily,
        hero_image_url: heroImageUrl || null,
        is_recurring: isRecurring
      };
      
      console.log('📅 Creating event with attendees:', eventData.attendees);

      let createdEvents: any[] = [];

      if (isRecurring && recurrencePattern !== 'none') {
        const recurringEvents = generateRecurringEvents(eventData, recurrencePattern);
        for (const recurringEvent of recurringEvents) {
          const created = await createEvent(recurringEvent, [], true); // Silent mode for recurring events
          if (created) {
            createdEvents.push(created);
          }
        }
      } else {
        const event = await createEvent(eventData);
        if (event) {
          createdEvents.push(event);
        }
      }

      if (createdEvents.length > 0) {
        // Handle public page creation if enabled
        if (isPublicSharingEnabled && createdEvents[0]) {
          try {
            const publicPageData = {
              hero_image_url: heroImageUrl,
              public_title: publicTitle || title,
              public_description: publicDescription || description,
              public_location: publicLocation || location,
              rsvp_enabled: rsvpEnabled
            };
            
            const publicPage = await createOrUpdatePublicPage(createdEvents[0].id, publicPageData);
            if (publicPage) {
              const url = generatePublicEventUrl(publicPage.slug);
              setPublicPageUrl(url);
            }
          } catch (publicError) {
            console.error('Error creating public page:', publicError);
          }
        }

        // Sync with Google Calendar if connected
        if (isConnected) {
          try {
            for (const event of createdEvents) {
              await syncEventToGoogleCalendar(event);
            }
            toast({
              title: "Événement créé",
              description: `${createdEvents.length} événement(s) créé(s) et synchronisé(s) avec Google Calendar.${isPublicSharingEnabled ? ' Page publique créée.' : ''}`,
            });
          } catch (syncError) {
            console.error('Google Calendar sync error:', syncError);
            toast({
              title: "Événement créé",
              description: `${createdEvents.length} événement(s) créé(s), mais la synchronisation Google Calendar a échoué.${isPublicSharingEnabled ? ' Page publique créée.' : ''}`,
              variant: "destructive",
            });
          }
        } else {
          toast({
            title: "Événement créé",
            description: `${createdEvents.length} événement(s) créé(s) avec succès.${isPublicSharingEnabled ? ' Page publique créée.' : ''}`,
          });
        }

        navigate('/calendar');
      }
    } catch (error) {
      console.error('Error creating event:', error);
      toast({
        variant: "destructive",
        title: "Erreur",
        description: "Impossible de créer l'événement.",
      });
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      toast({ title: 'Copié !' });
    } catch (error) {
      console.error('Failed to copy:', error);
    }
  };

  const shareOnWhatsApp = (url: string) => {
    const message = encodeURIComponent(`Vous êtes invité(e) à cet événement ! Confirmez votre présence : ${url}`);
    window.open(`https://wa.me/?text=${message}`, '_blank');
  };

  const eventTypeOptions = [
    { value: 'family', label: '👨‍👩‍👧‍👦 Famille', color: 'bg-purple-500' },
    { value: 'personal', label: '👤 Personnel', color: 'bg-blue-500' },
    { value: 'appointment', label: '👨‍⚕️ Rendez-vous', color: 'bg-green-500' },
    { value: 'work', label: '💼 Travail', color: 'bg-indigo-500' },
    { value: 'birthday', label: '🎂 Anniversaire', color: 'bg-pink-500' },
    { value: 'holiday', label: '🎉 Vacances', color: 'bg-orange-500' }
  ];

  if (!family) {
    return (
      <NoFamilyMessage 
        description="Vous devez d'abord créer une famille pour créer des événements"
        buttonText="Créer ma famille"
        variant="calendar"
      />
    );
  }

  return (
    <div className="flex flex-col min-h-screen">
      <PageHeader title="Créer un événement" backPath="/calendar" />

      <div className="flex-1 px-4 pt-2 pb-32">
        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="space-y-2">
            <Label htmlFor="title">Titre *</Label>
            <Input
              id="title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Titre de l'événement"
              required
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="description">Description</Label>
            <Textarea
              id="description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Description de l'événement"
              rows={3}
            />
          </div>

          <div className="space-y-2">
            <Label>Date *</Label>
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  className={cn(
                    "w-full justify-start text-left font-normal",
                    !startDate && "text-muted-foreground"
                  )}
                >
                  <CalendarIcon className="mr-2 h-4 w-4" />
                  {startDate ? format(startDate, "PPPP", { locale: fr }) : "Sélectionner une date"}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0">
                <Calendar
                  mode="single"
                  selected={startDate}
                  onSelect={setStartDate}
                  initialFocus
                />
              </PopoverContent>
            </Popover>
          </div>

          <div className="flex items-center space-x-2">
            <Checkbox
              id="all-day"
              checked={isAllDay}
              onCheckedChange={(checked) => setIsAllDay(checked === true)}
            />
            <Label htmlFor="all-day">Toute la journée</Label>
          </div>

          {!isAllDay && (
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="start-time">Heure de début</Label>
                <TimeSelector
                  id="start-time"
                  value={startTime}
                  onChange={setStartTime}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="end-time">Heure de fin</Label>
                <TimeSelector
                  id="end-time"
                  value={endTime}
                  onChange={setEndTime}
                />
              </div>
            </div>
          )}

          <div className="space-y-2">
            <Label htmlFor="event-type">Type d'événement</Label>
            <Select value={eventType} onValueChange={(value: any) => setEventType(value)}>
              <SelectTrigger>
                <SelectValue placeholder="Choisir le type" />
              </SelectTrigger>
              <SelectContent>
                {eventTypeOptions.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    <div className="flex items-center gap-2">
                      <div className={`w-3 h-3 rounded-full ${option.color}`}></div>
                      {option.label}
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="location">Lieu</Label>
            <LocationInput
              value={location}
              onChange={({ address, latitude: lat, longitude: lng }) => {
                setLocation(address);
                setLatitude(lat);
                setLongitude(lng);
              }}
              placeholder="Lieu de l'événement"
            />
          </div>

          <div className="flex items-center space-x-2">
            <Checkbox
              id="recurring"
              checked={isRecurring}
              onCheckedChange={(checked) => {
                setIsRecurring(checked === true);
                if (checked) {
                  setRecurrencePattern('daily');
                } else {
                  setRecurrencePattern('none');
                }
              }}
            />
            <Label htmlFor="recurring">Répéter cet événement</Label>
          </div>

          {isRecurring && (
            <div className="space-y-2">
              <Label htmlFor="recurrence">Fréquence</Label>
              <Select value={recurrencePattern} onValueChange={(value: RecurrencePattern) => setRecurrencePattern(value)}>
                <SelectTrigger>
                  <SelectValue placeholder="Choisir la fréquence" />
                </SelectTrigger>
                <SelectContent>
                  {RECURRENCE_OPTIONS_ACTIVE.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          <div className="space-y-3">
            <Label>Participants</Label>
            <div className="flex items-center space-x-2">
              <Checkbox
                id="invite-all"
                checked={inviteAllFamily}
                onCheckedChange={(checked) => setInviteAllFamily(checked === true)}
              />
              <Label htmlFor="invite-all" className="text-sm">
                Inviter toute la famille
              </Label>
            </div>

            {!inviteAllFamily && (
              <div className="space-y-2">
                <Label className="text-sm text-muted-foreground">Sélectionner les participants :</Label>
                <EventAttendeesSelector
                  contacts={Object.values(groupedContacts).flat()}
                  selectedContactIds={selectedAttendees}
                  onSelectionChange={setSelectedAttendees}
                  placeholder="Sélectionner les participants"
                />
              </div>
            )}
          </div>

          {/* Public Sharing Section */}
          <div className="space-y-4 p-4 border rounded-lg bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-950/20 dark:to-indigo-950/20 mb-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Share2 className="h-4 w-4 text-blue-600" />
                <Label className="font-medium">Partage public</Label>
              </div>
              <Switch
                checked={isPublicSharingEnabled}
                onCheckedChange={setIsPublicSharingEnabled}
              />
            </div>
            
            <p className="text-sm text-muted-foreground">
              Créez une page publique que vous pouvez partager avec des invités externes
            </p>

            {isPublicSharingEnabled && (
              <div className="space-y-4 mt-4 pt-4 border-t">
                {publicPageUrl && (
                  <div className="space-y-2">
                    <Label className="text-sm font-medium">Lien de partage</Label>
                    <div className="flex space-x-2">
                      <Input value={publicPageUrl} readOnly className="flex-1" />
                      <Button variant="outline" size="icon" onClick={() => copyToClipboard(publicPageUrl)}>
                        <Copy className="h-4 w-4" />
                      </Button>
                    </div>
                    <div className="flex space-x-2">
                      <Button variant="outline" className="flex-1" onClick={() => window.open(publicPageUrl, '_blank')}>
                        <ExternalLink className="h-4 w-4 mr-2" /> Voir la page
                      </Button>
                      <Button variant="outline" className="flex-1" onClick={() => shareOnWhatsApp(publicPageUrl)}>
                        <MessageSquare className="h-4 w-4 mr-2" /> WhatsApp
                      </Button>
                    </div>
                  </div>
                )}

                <Separator />

                <div className="space-y-4">
                  <Label className="text-sm font-medium">Personnalisation de la page publique</Label>
                  
                  <div className="space-y-2">
                    <Label htmlFor="hero-image" className="text-sm">Image de couverture</Label>
                    <ImageUploader
                      currentImageUrl={heroImageUrl}
                      onImageUploaded={setHeroImageUrl}
                      bucketPath="public-events"
                      size="banner"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="public-title" className="text-sm">Titre public</Label>
                    <Input
                      id="public-title"
                      value={publicTitle}
                      onChange={(e) => setPublicTitle(e.target.value)}
                      placeholder="Titre visible pour les invités"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="public-description" className="text-sm">Description publique</Label>
                    <Textarea
                      id="public-description"
                      value={publicDescription}
                      onChange={(e) => setPublicDescription(e.target.value)}
                      placeholder="Description visible pour les invités"
                      rows={3}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="public-location" className="text-sm">Lieu public</Label>
                    <Input
                      id="public-location"
                      value={publicLocation}
                      onChange={(e) => setPublicLocation(e.target.value)}
                      placeholder="Lieu visible pour les invités"
                    />
                  </div>

                  <div className="flex items-center justify-between">
                    <Label htmlFor="rsvp-enabled" className="text-sm">Permettre les confirmations de présence (RSVP)</Label>
                    <Switch
                      id="rsvp-enabled"
                      checked={rsvpEnabled}
                      onCheckedChange={setRsvpEnabled}
                    />
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Google Calendar Integration */}
          <div className="space-y-3 p-4 border rounded-lg bg-gradient-to-r from-primary/5 to-accent/5 mb-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <CalendarIcon className="h-4 w-4 text-primary" />
                <Label className="font-medium">Synchronisation Google Calendar</Label>
              </div>
              {isConnected && (
                <div className="flex items-center gap-1 text-green-600">
                  <CheckCircle className="h-4 w-4" />
                  <span className="text-sm">Connecté</span>
                </div>
              )}
            </div>
            
            {isConnected ? (
              <p className="text-sm text-muted-foreground">
                L'événement sera automatiquement ajouté à votre Google Calendar.
              </p>
            ) : (
              <div className="space-y-2">
                <p className="text-sm text-muted-foreground">
                  Connectez-vous à Google Calendar pour synchroniser automatiquement vos événements.
                </p>
                <Button 
                  type="button" 
                  variant="outline" 
                  size="sm" 
                  onClick={connectToGoogleCalendar}
                  className="gap-2"
                >
                  <CalendarIcon className="h-4 w-4" />
                  Se connecter à Google Calendar
                </Button>
              </div>
            )}
          </div>
        </form>
      </div>

      <div className="fixed bottom-0 left-0 right-0 p-4 border-t bg-background space-y-3 z-50">
        <Button 
          onClick={handleSubmit}
          disabled={loading || !title.trim() || !startDate}
          className="w-full"
          variant={primaryVariant as any}
        >
          {loading ? "Création..." : "Créer l'événement"}
        </Button>
        <Button 
          variant="cancel-hover" 
          onClick={() => navigate('/calendar')}
          className="w-full"
          type="button"
        >
          Annuler
        </Button>
      </div>
    </div>
  );
}